import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    FlexBox,
    FormError,
    FormField,
    FormInputGroup,
    Icon,
    StandardForm,
    Typography,
    responsive,
    useScreenClass,
} from '@vp/swan';
import { __ } from '@99designs/i18n';
import { sendNavigationEvent, sendPresetClickedTrackingEvent, usePage } from '@99designs/tracking';
import DesignLines from '../Images/DesignLines';
import { ResizableTextArea, ResizableTextAreaRef } from './ResizableTextArea';
import { GenerateMutationFn } from './brief.generated';

const ResponsiveBox = responsive(Box);

export const CHAR_LIMIT = 2000;

export interface PromptInputProps {
    briefId: string;
    generate: GenerateMutationFn;
    clientConfiguredValue: string;
}

export function PromptInputField({ briefId, generate, clientConfiguredValue }: PromptInputProps) {
    const { pageData } = usePage();
    const briefPath = `/experts/request/brief/${briefId}`;
    const pageZone = 'Start Brief Section';
    const charLimit = CHAR_LIMIT;

    const [promptText, setPromptText] = useState(clientConfiguredValue);
    const [isCharCountExceeded, setIsCharCountExceeded] = useState(false);

    const screenClass = useScreenClass();
    const textAreaRef = useRef<ResizableTextAreaRef>(null);

    useEffect(() => {
        if (textAreaRef.current) {
            textAreaRef.current.resize();
        }

        setIsCharCountExceeded(promptText.length > charLimit);
    }, [promptText, screenClass]);

    useEffect(() => {
        setPromptText(clientConfiguredValue);
    }, [clientConfiguredValue]);

    return (
        <>
            <Box
                style={{
                    width: '100%',
                    height: 'auto',
                }}
            >
                <StandardForm>
                    <FormField>
                        <FormInputGroup>
                            <Box
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto',
                                    alignItems: 'end',
                                    width: '100%',
                                }}
                            >
                                <Box
                                    style={{
                                        position: 'relative',
                                        resize: 'none',
                                        overflow: 'visible',
                                        gridColumn: '1 / -1',
                                        width: '100%',
                                    }}
                                >
                                    <ResizableTextArea
                                        name="prompt-input"
                                        data-testid="prompt-input"
                                        ref={textAreaRef}
                                        rows={1}
                                        pb={10}
                                        pr={5}
                                        pt={4}
                                        style={{
                                            resize: 'none',
                                            overflow: 'auto',
                                            width: '100%',
                                        }}
                                        placeholder={__(
                                            "E.g. I need a business card design for my Italian restaurant, Nonna's Kitchen. We're a cozy, family-friendly neighborhood spot specializing in homemade pasta. Looking for something warm and inviting that feels like you're dining at grandma's house",
                                            {
                                                _context:
                                                    'This is an example placeholder for project description',
                                            }
                                        )}
                                        value={promptText}
                                        onChange={(e) => {
                                            setPromptText(e.target.value);
                                        }}
                                        autoFocus={true}
                                        fullWidth
                                        aria-labelledby="project-overview"
                                        aria-describedby="prompt-input-error"
                                    />
                                    <DesignLines />
                                </Box>
                                <Box
                                    style={{
                                        gridColumn: '1',
                                        justifySelf: 'start',
                                        marginTop: '-10em',
                                    }}
                                >
                                    <Typography
                                        mt={{ xs: 10, sm: 4 }}
                                        m={4}
                                        textColor={isCharCountExceeded ? 'error' : 'subtle'}
                                        fontSkin={'body-small'}
                                        style={{
                                            zIndex: 10,
                                            position: 'relative',
                                        }}
                                    >
                                        {charCounter(promptText, charLimit)}
                                    </Typography>
                                </Box>
                                <Box
                                    style={{
                                        gridColumn: '2',
                                        justifySelf: 'end',
                                        marginTop: '-10em',
                                    }}
                                >
                                    <Button
                                        iconPosition="left"
                                        mt={{ xs: 10, sm: 4 }}
                                        skin="primary"
                                        m={4}
                                        p={4}
                                        disabled={isCharCountExceeded}
                                        onClick={() => {
                                            sendNavigationEvent({
                                                category: pageData.pageSection,
                                                label: `Content: ${pageZone}`,
                                                eventDetail: `${window.location.pathname}${window.location.search};${briefPath}${window.location.search};${pageZone};designBriefStartButton`,
                                                navigationDetail: 'designBriefStartButton',
                                                ...pageData,
                                            });
                                            generate({
                                                variables: {
                                                    id: briefId,
                                                    input: promptText,
                                                },
                                            });
                                        }}
                                    >
                                        <Icon iconType="polish" />
                                        {clientConfiguredValue
                                            ? __('Start new brief', {
                                                  _context:
                                                      'This is the button text to start a new project brief, after the customer has already started one',
                                              })
                                            : __('Start brief', {
                                                  _context:
                                                      'This is the button text to start the project brief',
                                              })}
                                    </Button>
                                </Box>
                            </Box>
                            {isCharCountExceeded && (
                                <FormError role={'alert'} mt={3} id="prompt-input-error">
                                    {__(`You have exceeded the {{charLimit}} character limit`, {
                                        charLimit,
                                        _context:
                                            'This is the error message when the character limit is exceeded in a text field in a form',
                                    })}
                                </FormError>
                            )}
                        </FormInputGroup>
                    </FormField>
                </StandardForm>
            </Box>
            <PromptPresets setPromptText={setPromptText} />
        </>
    );
}

function PromptPresets({ setPromptText }: { setPromptText: (text: string) => void }) {
    const iconBaseUrl =
        'https://cms.cloudinary.vpsvc.com/image/upload/fl_sanitize,q_auto/expert_services/Brief/Intelligent%20Brief';
    return (
        <>
            <Box style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}>
                <Typography fontSkin={'body-small-bold'}>Common design requests</Typography>
            </Box>
            <ResponsiveBox
                xs={{
                    style: {
                        display: 'grid',
                        gridAutoFlow: 'row',
                        gap: '8px',
                        width: '100%',
                    },
                }}
                sm={{
                    style: {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'flex-start',
                    },
                }}
            >
                <PresetButton
                    trackingName={'Recreate a design'}
                    setPromptText={() =>
                        setPromptText(
                            __(
                                "I'd like to recreate a design I have, but I need it updated for printing. I want to keep [specific elements] while refreshing [elements to update]. I'll upload an image of the original for reference.",
                                {
                                    _context:
                                        'This is a template for a design input that is prefilled when the customer hits the preset button. The [] are the parts that the customer should fill in with their own information.',
                                }
                            )
                        )
                    }
                    iconSrc={`${iconBaseUrl}/Recreate.svg`}
                >
                    {__('Recreate a design')}
                </PresetButton>
                <PresetButton
                    trackingName={'Celebration visuals'}
                    setPromptText={() =>
                        setPromptText(
                            __(
                                'I need a design for my event that reflects the [theme/mood], appeals to my audience, and clearly communicates the essential details on a design. The event is for [purpose/occasion] and should convey [specific feeling or message]. Important information to include: [date/time/location/contact].',
                                {
                                    _context:
                                        'This is a template for a design input that is prefilled when the customer hits the preset button. The [] are the parts that the customer should fill in with their own information.',
                                }
                            )
                        )
                    }
                    iconSrc={`${iconBaseUrl}/Celebrate.svg`}
                >
                    {__('Celebration visuals')}
                </PresetButton>
                <PresetButton
                    trackingName={'Designs for an event'}
                    setPromptText={() =>
                        setPromptText(
                            __(
                                'I need a design for my upcoming [type of event]. I’m looking for something that captures the [mood/theme] and effectively communicates [key information]. The event is for [target audience] and will take place [timeframe].',
                                {
                                    _context:
                                        'This is a template for a design input that is prefilled when the customer hits the preset button. The [] are the parts that the customer should fill in with their own information.',
                                }
                            )
                        )
                    }
                    iconSrc={`${iconBaseUrl}/Event.svg`}
                >
                    {__('Designs for an event')}
                </PresetButton>
                <PresetButton
                    trackingName={'Highlight an offer'}
                    setPromptText={() =>
                        setPromptText(
                            __(
                                'I want to spotlight my [type of offer] with a design that attracts customers and drives action. The offer includes [key details], is available [timeframe], and should emphasize [main benefit/urgency].',
                                {
                                    _context:
                                        'This is a template for a design input that is prefilled when the customer hits the preset button. The [] are the parts that the customer should fill in with their own information.',
                                }
                            )
                        )
                    }
                    iconSrc={`${iconBaseUrl}/Offer.svg`}
                >
                    {__('Highlight an offer')}
                </PresetButton>
            </ResponsiveBox>
        </>
    );
}

function PresetButton({
    trackingName,
    iconSrc,
    setPromptText,
    children,
}: {
    trackingName: string;
    iconSrc: string;
    setPromptText: () => void;
    children: React.ReactNode;
}) {
    const { pageData } = usePage();
    return (
        <Button
            onClick={() => {
                setPromptText();
                sendPresetClickedTrackingEvent({
                    label: trackingName,
                    ...pageData,
                });
            }}
            iconPosition={'left'}
        >
            <Icon src={iconSrc} size={'small'} />
            <Typography fontSkin={'body-small-bold'}>{children}</Typography>
        </Button>
    );
}

function charCounter(text: string, charLimit: number): string {
    const charRemaining = charLimit - text.length;
    if (charRemaining > 100) {
        return '';
    }
    if (charRemaining === 1) {
        return __('1 character remaining', {
            _context:
                'This is the message for 1 character remaining in a text field, which differs from the plural form message',
        });
    }
    return __(`{{charRemaining}} characters remaining`, {
        charRemaining,
        _context: 'This is the message for characters remaining in a text field',
    });
}
